"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Bell, Globe, Shield, Mail, Moon, Sun, Monitor } from "lucide-react";

export default function TalentSettings() {
  const { t, i18n } = useTranslation(["talent", "common"]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    jobAlerts: true,
    applicationUpdates: true,
    marketingEmails: false,
    theme: "system",
    language: i18n.language
  });

  // Sync component state with i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setSettings((prev) => ({ ...prev, language: lng }));
    };

    // Listen for i18n language changes
    i18n.on("languageChanged", handleLanguageChange);

    // Sync initial state with current i18n language
    setSettings((prev) => ({ ...prev, language: i18n.language }));

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  const handleSave = async () => {
    try {
      // In a real app, save settings to backend
      toast({
        title: t("common:status.success"),
        description: t("talent:settings.save_success")
      });
    } catch (error) {
      toast({
        title: t("common:status.error"),
        description: t("talent:settings.save_error"),
        variant: "destructive"
      });
    }
  };

  const handleLanguageChange = async (language: string) => {
    try {
      // Change language and wait for it to complete
      await i18n.changeLanguage(language);

      // Update local state
      setSettings((prev) => ({ ...prev, language }));

      // Show success toast
      const languageName = language === "de" ? "Deutsch" : "English";
      toast({
        title: t("common:status.success"),
        description: t("talent:settings.language_changed", {
          language: languageName,
          defaultValue: `Language changed to ${languageName}`
        })
      });
    } catch (error) {
      console.error("Error changing language:", error);
      toast({
        title: t("common:status.error"),
        description: t("talent:settings.language_error", {
          defaultValue: "Failed to change language"
        }),
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t("talent:settings.notifications.title")}
            </CardTitle>
            <CardDescription>
              {t("talent:settings.notifications.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-notifications">
                  {t("talent:settings.notifications.email_notifications.title")}
                </Label>
                <p className="text-sm text-gray-600">
                  {t(
                    "talent:settings.notifications.email_notifications.description"
                  )}
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={settings.emailNotifications}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({
                    ...prev,
                    emailNotifications: checked
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="job-alerts">
                  {t("talent:settings.notifications.job_alerts.title")}
                </Label>
                <p className="text-sm text-gray-600">
                  {t("talent:settings.notifications.job_alerts.description")}
                </p>
              </div>
              <Switch
                id="job-alerts"
                checked={settings.jobAlerts}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, jobAlerts: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="application-updates">
                  {t("talent:settings.notifications.application_updates.title")}
                </Label>
                <p className="text-sm text-gray-600">
                  {t(
                    "talent:settings.notifications.application_updates.description"
                  )}
                </p>
              </div>
              <Switch
                id="application-updates"
                checked={settings.applicationUpdates}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({
                    ...prev,
                    applicationUpdates: checked
                  }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="marketing-emails">
                  {t("talent:settings.notifications.marketing_emails.title")}
                </Label>
                <p className="text-sm text-gray-600">
                  {t(
                    "talent:settings.notifications.marketing_emails.description"
                  )}
                </p>
              </div>
              <Switch
                id="marketing-emails"
                checked={settings.marketingEmails}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, marketingEmails: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Appearance & Language */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t("talent:settings.appearance.title")}
            </CardTitle>
            <CardDescription>
              {t("talent:settings.appearance.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">
                {t("talent:settings.appearance.language")}
              </Label>
              <Select
                value={settings.language}
                onValueChange={handleLanguageChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  <SelectItem value="en">🇺🇸 English</SelectItem>
                  <SelectItem value="es">🇪🇸 Español</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme">
                {t("talent:settings.appearance.theme")}
              </Label>
              <Select
                value={settings.theme}
                onValueChange={(value) =>
                  setSettings((prev) => ({ ...prev, theme: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">
                    <div className="flex items-center gap-2">
                      <Sun className="h-4 w-4" />
                      {t("talent:settings.appearance.theme_options.light")}
                    </div>
                  </SelectItem>
                  <SelectItem value="dark">
                    <div className="flex items-center gap-2">
                      <Moon className="h-4 w-4" />
                      {t("talent:settings.appearance.theme_options.dark")}
                    </div>
                  </SelectItem>
                  <SelectItem value="system">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4" />
                      {t("talent:settings.appearance.theme_options.system")}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t("talent:settings.privacy.title")}
            </CardTitle>
            <CardDescription>
              {t("talent:settings.privacy.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.privacy.change_password")}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => navigate("/account/manage-cookies")}
            >
              {t("common:cookies.management.title")}
            </Button>
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.privacy.two_factor")}
            </Button>
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.privacy.download_data")}
            </Button>
            <Button variant="destructive" className="w-full justify-start">
              {t("talent:settings.privacy.delete_account")}
            </Button>
          </CardContent>
        </Card>

        {/* Support */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t("talent:settings.support.title")}
            </CardTitle>
            <CardDescription>
              {t("talent:settings.support.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.support.help_center")}
            </Button>
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.support.contact_support")}
            </Button>
            <Button variant="outline" className="w-full justify-start">
              {t("talent:settings.support.give_feedback")}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave}>
          {t("talent:settings.save_settings")}
        </Button>
      </div>
    </div>
  );
}
